import React, { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { Icon } from 'semantic-ui-react';
import { logOut, setCredentials } from '../auth/authSlice';
import { useGetMeQuery } from '../auth/authApiSlice';
import styles from './PendingApprovalPage.module.css';
import { BRAND } from '../../brand';

// Client copy, verbatim (ACE Academy, 2026-09). Exported so the test can pin the wording.
export const PENDING_COPY = {
  lead: `${BRAND}는 ACE challenge의 2.0버전입니다.`,
  // One sentence, broken at the clause so it never wraps mid-phrase.
  bodyLines: ['AI 가상 고객과 롤플레이를 하고', '고객 경험에 있어 나의 강점과 개선점을 알아볼 수 있습니다.'],
  soon: '곧 만나요!',
  hint: '틈틈이 ACE 콘텐츠를 학습하며 기다려주세요.',
};

// The four ACE phases. Gradient stops are the exact shading functions from the client's
// "ACE training 자료 발췌 (색감)" deck; subtitles are from their pending-page mockup.
export const PHASES = [
  { name: 'Connect', desc: '공감대 형성을 통한 신뢰 쌓기', from: '#d31e32', to: '#ff74c4' },
  { name: 'Discover', desc: '고객에 대해 자세하게 알아보기', from: '#da5c09', to: '#efb730' },
  { name: 'Share', desc: '정보와 경험의 기회 공유하기', from: '#176fd4', to: '#61fffa' },
  { name: 'Close', desc: '고객이 확신을 갖도록 돕기', from: '#149503', to: '#d7ff72' },
];

const PendingApprovalPage = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const user = useSelector((state) => state.auth.user);
  const token = useSelector((state) => state.auth.token);
  const username = useSelector((state) => state.auth.username);
  const partner = useSelector((state) => state.auth.partner);

  // Re-check approval status on mount (e.g. when the user refreshes this page):
  // if they've since been approved, refresh stored credentials and send them in.
  const { data: me } = useGetMeQuery(undefined, { refetchOnMountOrArgChange: true });
  useEffect(() => {
    if (me?.is_approved) {
      dispatch(setCredentials({ access: token, user: me, username, partner }));
      navigate('/landing');
    }
  }, [me, token, username, partner, dispatch, navigate]);

  const handleLogout = () => {
    dispatch(logOut());
    navigate('/login');
  };

  return (
    <div className={styles.page}>
      {/* Fixed top bar */}
      <div className={styles.topbar}>
        <div className={styles.brand}>
          <img src="/ace-logo.png" alt={BRAND} className={styles.logo} />
          {BRAND}
        </div>
        <button className={styles.signout} onClick={handleLogout}>
          <Icon name="sign out" />
          로그아웃
        </button>
      </div>

      {/* Centered stage wrapper */}
      <div className={styles.stage}>
        {/* Main card */}
        <div className={styles.card}>
          <div className={styles.copy}>
            <h1 className={styles.lead}>{PENDING_COPY.lead}</h1>
            <p className={styles.body}>
              {PENDING_COPY.bodyLines[0]}
              <br />
              {PENDING_COPY.bodyLines[1]}
            </p>
            <p className={styles.soon}>{PENDING_COPY.soon}</p>
            <p className={styles.hint}>{PENDING_COPY.hint}</p>
          </div>

          <div className={styles.panel}>
            <div className={styles.grid}>
              {PHASES.map((p) => (
                <div key={p.name} className={styles.phase}>
                  <span
                    className={styles.phaseName}
                    style={{ backgroundImage: `linear-gradient(90deg, ${p.from}, ${p.to})` }}
                  >
                    {p.name}
                  </span>
                  <span className={styles.phaseDesc}>{p.desc}</span>
                </div>
              ))}
            </div>
          </div>

          {user && (
            <div className={styles.account}>
              <p className={styles.accountLine}>{user.full_name || user.username}</p>
              <p className={styles.accountLine}>{user.email}</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default PendingApprovalPage;
